/**
 * 운영 도구 - 가족 정보 변경
 * QR 스캔 또는 전화번호로 회원 조회 후 자녀 추가
 */

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import QRScanner from '../components/QRScanner';
import StoreBadge from '../components/StoreBadge';
import { getMemberByQR, getMemberByPhone, getFamilyMembers, addChildMember, removeChildMember, updateMember, registerMemberOnly } from '../services/api';
import './FamilyManagePage.css';

import { isBridgeApp } from '../utils/isBridgeApp';
const FamilyManagePage = () => {
  const navigate = useNavigate();

  // 검색 상태
  const [scanning, setScanning] = useState(true);
  const [phoneInput, setPhoneInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // 회원/가족 데이터
  const [member, setMember] = useState(null);
  const [familyData, setFamilyData] = useState(null);

  // 신규 가입 흐름
  const [showRegister, setShowRegister] = useState(false);
  const [registerPhone, setRegisterPhone] = useState('');
  const [registering, setRegistering] = useState(false);

  // 자녀 추가 폼
  const [showAddForm, setShowAddForm] = useState(false);
  const [nickname, setNickname] = useState('');
  const [birthday, setBirthday] = useState('');
  const [adding, setAdding] = useState(false);
  const [result, setResult] = useState(null);

  // 자녀 편집
  const [editingChild, setEditingChild] = useState(null); // { member_id, nickname, birthday }
  const [editNickname, setEditNickname] = useState('');
  const [editBirthday, setEditBirthday] = useState('');
  const [saving, setSaving] = useState(false);

  // 회원 조회 후 가족 데이터 로드
  const loadFamilyData = useCallback(async (memberData) => {
    try {
      const res = await getFamilyMembers(memberData.member_id);
      if (res.success) {
        setFamilyData(res);
      } else {
        // 가족 없음 (SINGLE) - 기본 데이터
        setFamilyData({
          parent: {
            member_id: memberData.member_id,
            name: memberData.name,
            phone: memberData.phone,
            balance: memberData.balance || 0,
            member_type: memberData.member_type || 'SINGLE',
          },
          children: [],
          children_count: 0,
          total_family_balance: memberData.balance || 0,
        });
      }
    } catch {
      setFamilyData({
        parent: {
          member_id: memberData.member_id,
          name: memberData.name,
          phone: memberData.phone,
          balance: memberData.balance || 0,
          member_type: 'SINGLE',
        },
        children: [],
        children_count: 0,
        total_family_balance: memberData.balance || 0,
      });
    }
  }, []);

  // QR 스캔 처리
  const handleScan = useCallback(async (qrCode) => {
    if (loading) return;

    setScanning(false);
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const res = await getMemberByQR(qrCode);
      const memberData = res.member;
      setMember(memberData);
      await loadFamilyData(memberData);
    } catch (err) {
      setError(err.message || '회원 정보를 조회할 수 없습니다');
      setMember(null);
      setFamilyData(null);
    } finally {
      setLoading(false);
    }
  }, [loading, loadFamilyData]);

  // 전화번호 검색
  const handlePhoneSearch = async () => {
    if (phoneInput.length !== 8) return;
    const fullPhone = '010' + phoneInput;

    setScanning(false);
    setLoading(true);
    setError(null);
    setResult(null);
    setShowRegister(false);

    try {
      const res = await getMemberByPhone(fullPhone);
      const memberData = res.member;
      setMember(memberData);
      await loadFamilyData(memberData);
    } catch (err) {
      // 회원 없음 → 신규 가입 화면으로 전환
      setMember(null);
      setFamilyData(null);
      setRegisterPhone(fullPhone);
      setShowRegister(true);
    } finally {
      setLoading(false);
    }
  };

  // 신규 회원 가입 처리
  const handleRegister = async () => {
    if (registerPhone.length !== 11) return;

    setRegistering(true);
    setError(null);
    setResult(null);

    try {
      const storeId = localStorage.getItem('store_id');
      const res = await registerMemberOnly(registerPhone, storeId);
      const memberData = res.member;
      setMember(memberData);
      setShowRegister(false);
      setResult({ success: true, message: '멤버십 가입 완료! 자녀를 추가해 주세요.' });
      await loadFamilyData(memberData);
    } catch (err) {
      setError(err.message || '회원 가입에 실패했습니다');
    } finally {
      setRegistering(false);
    }
  };

  // 전화번호 수정 후 다시 검색
  const handleFixPhone = () => {
    setShowRegister(false);
    // registerPhone은 '010...'인 전체 번호이므로, 뒤 8자리만 복원
    setPhoneInput(registerPhone.startsWith('010') ? registerPhone.slice(3) : registerPhone);
    setScanning(true);
  };

  // 자녀 추가
  const handleAddChild = async () => {
    // 닉네임 비어도 OK (서버에서 자녀1, 자녀2 자동 생성)
    const parentId = familyData?.parent?.member_id || member.member_id;

    setAdding(true);
    setResult(null);

    try {
      const res = await addChildMember(parentId, {
        nickname: nickname.trim(),
        name: null,
        birthday: birthday || null,
      });

      if (res.success) {
        setResult({ success: true, message: `자녀 '${nickname.trim()}' 추가 완료!` });
        setNickname('');
        setBirthday('');
        setShowAddForm(false);
        // 가족 데이터 새로고침
        await loadFamilyData(member);
      }
    } catch (err) {
      setResult({ success: false, message: err.message || '자녀 추가에 실패했습니다' });
    } finally {
      setAdding(false);
    }
  };

  // 자녀 편집 시작
  const handleEditChild = (child) => {
    setEditingChild(child.member_id);
    setEditNickname(child.nickname || '');
    setEditBirthday(child.birthday || '');
    setResult(null);
    // 키보드가 올라온 후 편집 영역으로 스크롤
    setTimeout(() => {
      const el = document.querySelector('.fm-child-edit');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 300);
  };

  // 자녀 편집 저장
  const handleSaveEdit = async () => {
    if (!editingChild || !editNickname.trim()) return;
    setSaving(true);
    try {
      await updateMember(editingChild, {
        nickname: editNickname.trim(),
        birthday: editBirthday || null,
      });
      setResult({ success: true, message: `'${editNickname.trim()}' 수정 완료!` });
      setEditingChild(null);
      await loadFamilyData(member);
    } catch (err) {
      setResult({ success: false, message: err.message || '수정에 실패했습니다' });
    } finally {
      setSaving(false);
    }
  };

  // 자녀 삭제
  const handleRemoveChild = async (child) => {
    const name = child.nickname || child.name || '자녀';
    if (!window.confirm(`'${name}'을(를) 삭제하시겠습니까?\n잔액은 보호자에게 이전됩니다.`)) return;

    try {
      await removeChildMember(familyData.parent.member_id, child.member_id);
      setResult({ success: true, message: `'${name}' 삭제 완료 (잔액 이전됨)` });
      await loadFamilyData(member);
    } catch (err) {
      setResult({ success: false, message: err.message || '삭제에 실패했습니다' });
    }
  };

  // 처음으로 (재검색)
  const handleReset = () => {
    setMember(null);
    setFamilyData(null);
    setError(null);
    setResult(null);
    setShowAddForm(false);
    setShowRegister(false);
    setRegisterPhone('');
    setRegistering(false);
    setNickname('');
    setBirthday('');
    setPhoneInput('');
    setScanning(true);
  };

  const formatPhone = (phone) => {
    if (!phone || phone.length !== 11) return phone || '';
    return phone.slice(0, 3) + '-' + phone.slice(3, 7) + '-' + phone.slice(7);
  };

  const maskPhone = (phone) => {
    if (!phone || phone.length < 8) return phone || '-';
    return phone.slice(0, 3) + '-****-' + phone.slice(-4);
  };

  return (
    <div className="fm-page">
      {/* 헤더 */}
      <header className="fm-header">
        <button className="btn-back" onClick={() => navigate('/')}>
          ← 메뉴
        </button>
        <div className="fm-header-center" onClick={() => {
          // 웹(크롬)에서는 전체화면으로 가지 않는다 — 태블릿 앱(ZlzlKiosk)에서만 (2026-08-06 요구).
          if (!isBridgeApp()) return;
          const el = document.documentElement;
          (el.requestFullscreen || el.webkitRequestFullscreen)?.call(el)?.catch?.(() => {});
        }}>
          <h1>👨‍👩‍👧‍👦 가족 정보 변경</h1>
          <StoreBadge />
        </div>
        <div className="fm-header-spacer" />
      </header>

      <main className="fm-content">
        {/* 검색 모드 */}
        {scanning && !loading && (
          <div className="fm-search-section">
            {/* 전화번호 검색 */}
            <div className="fm-phone-search">
              <p className="fm-search-label">전화번호로 검색</p>
              <div className="fm-phone-display">
                <span style={{color: '#999'}}>010</span>
                {phoneInput.length > 0 ? `-${phoneInput.slice(0, 4)}` : '-'}
                {phoneInput.length > 4 ? `-${phoneInput.slice(4)}` : ''}
                {phoneInput.length === 0 && <span style={{color: '#bbb'}}> 뒤 8자리 입력</span>}
              </div>
              {error && (
                <div className="fm-phone-error">{error}</div>
              )}
              <div className="fm-numpad">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(n => (
                  <button
                    key={n}
                    type="button"
                    className="fm-numpad-btn"
                    onClick={() => { setError(null); phoneInput.length < 8 && setPhoneInput(prev => prev + n); }}
                  >
                    {n}
                  </button>
                ))}
                <button
                  type="button"
                  className="fm-numpad-btn fm-numpad-clear"
                  onClick={() => { setError(null); setPhoneInput(''); }}
                >
                  전체삭제
                </button>
                <button
                  type="button"
                  className="fm-numpad-btn"
                  onClick={() => { setError(null); phoneInput.length < 8 && setPhoneInput(prev => prev + '0'); }}
                >
                  0
                </button>
                <button
                  type="button"
                  className="fm-numpad-btn fm-numpad-backspace"
                  onClick={() => { setError(null); setPhoneInput(prev => prev.slice(0, -1)); }}
                >
                  ⌫
                </button>
              </div>
              <button
                className="fm-phone-btn"
                onClick={handlePhoneSearch}
                disabled={phoneInput.length !== 8}
              >
                검색
              </button>
            </div>

            <div className="fm-divider">
              <span>또는</span>
            </div>

            {/* QR 스캔 */}
            <div className="fm-qr-section">
              <p className="fm-search-label">QR 코드 스캔</p>
              <QRScanner onScan={handleScan} enabled={scanning} />
            </div>
          </div>
        )}

        {/* 로딩 */}
        {loading && (
          <div className="fm-loading">
            <div className="fm-spinner" />
            <p>회원 정보 조회 중...</p>
          </div>
        )}

        {/* 신규 가입 확인 */}
        {showRegister && !loading && !member && (
          <div className="fm-register-section">
            <div className="fm-register-card">
              <span className="fm-register-icon">📋</span>
              <h2 className="fm-register-title">등록되지 않은 번호입니다</h2>
              <p className="fm-register-desc">
                아래 전화번호로 멤버십에 가입하시겠습니까?<br />
                번호가 맞는지 다시 확인해 주세요.
              </p>
              <div className="fm-register-phone">
                {formatPhone(registerPhone)}
              </div>
              {error && (
                <div className="fm-result-msg fail">❌ {error}</div>
              )}
              <div className="fm-register-buttons">
                <button
                  className="fm-btn-fix-phone"
                  onClick={handleFixPhone}
                  disabled={registering}
                >
                  번호 수정
                </button>
                <button
                  className="fm-btn-register"
                  onClick={handleRegister}
                  disabled={registering}
                >
                  {registering ? '가입 중...' : '가입 진행'}
                </button>
              </div>
              <button className="fm-btn-action fm-btn-back-search" onClick={handleReset}>
                ← 처음으로
              </button>
            </div>
          </div>
        )}

        {/* 에러 (신규 가입 흐름이 아닌 일반 에러) */}
        {error && !loading && !member && !showRegister && (
          <div className="fm-error-card">
            <span className="fm-error-icon">⚠️</span>
            <p>{error}</p>
            <button className="fm-btn-action" onClick={handleReset}>
              다시 검색
            </button>
          </div>
        )}

        {/* 회원 + 가족 정보 */}
        {member && !loading && familyData && (
          <div className="fm-result-section">
            {/* 부모(대표) 정보 카드 */}
            <div className="fm-member-card">
              <div className="fm-member-header">
                <span className="fm-member-avatar">👤</span>
                <div>
                  <h2 className="fm-member-name">
                    {familyData.parent.name || '이름 없음'}
                  </h2>
                  <span className="fm-member-phone">
                    {maskPhone(familyData.parent.phone)}
                  </span>
                </div>
                <span className="fm-member-type-badge">
                  {familyData.parent.member_type === 'CHILD' ? '자녀' : '대표'}
                </span>
              </div>

              <div className="fm-member-stats">
                <div className="fm-stat">
                  <span className="fm-stat-label">가족 수</span>
                  <span className="fm-stat-value">{(familyData.children_count || 0) + 1}명</span>
                </div>
                <div className="fm-stat">
                  <span className="fm-stat-label">총 주화</span>
                  <span className="fm-stat-value">🪙 {(familyData.total_family_balance || 0).toLocaleString()}</span>
                </div>
              </div>
            </div>

            <div className="fm-right-area">
            {/* 결과 메시지 */}
            {result && (
              <div className={`fm-result-msg ${result.success ? 'success' : 'fail'}`}>
                {result.success ? '✅' : '❌'} {result.message}
              </div>
            )}

            {/* 기존 자녀 목록 */}
            {familyData.children.length > 0 && (
              <div className="fm-children-section">
                <h3 className="fm-section-title">
                  👧👦 등록된 자녀 ({familyData.children.length}명)
                </h3>
                <div className="fm-children-list">
                  {familyData.children.map((child, idx) => (
                    <div key={child.member_id || idx} className="fm-child-item">
                      {editingChild === child.member_id ? (
                        /* 편집 모드 */
                        <div className="fm-child-edit">
                          <input
                            type="text"
                            className="fm-edit-input"
                            value={editNickname}
                            onChange={(e) => setEditNickname(e.target.value)}
                            onFocus={(e) => setTimeout(() => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300)}
                            placeholder="닉네임"
                            maxLength={50}
                            autoFocus
                          />
                          <input
                            type="date"
                            className="fm-edit-input"
                            value={editBirthday}
                            onChange={(e) => setEditBirthday(e.target.value)}
                          />
                          <div className="fm-edit-buttons">
                            <button className="fm-btn-edit-save" onClick={handleSaveEdit} disabled={saving || !editNickname.trim()}>
                              {saving ? '저장중' : '저장'}
                            </button>
                            <button className="fm-btn-edit-cancel" onClick={() => setEditingChild(null)}>취소</button>
                          </div>
                        </div>
                      ) : (
                        /* 표시 모드 */
                        <>
                          <div className="fm-child-avatar">
                            {child.nickname?.charAt(0) || '자'}
                          </div>
                          <div className="fm-child-info">
                            <span className="fm-child-nickname">{child.nickname}</span>
                            <span className="fm-child-balance">🪙 {(child.balance || 0).toLocaleString()}개</span>
                          </div>
                          <div className="fm-child-actions">
                            <button className="fm-btn-child-edit" onClick={() => handleEditChild(child)}>✏️</button>
                            <button className="fm-btn-child-delete" onClick={() => handleRemoveChild(child)}>🗑️</button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 자녀 추가 폼 */}
            {showAddForm ? (
              <div className="fm-add-form">
                <h3 className="fm-form-title">➕ 자녀 추가</h3>
                <div className="fm-form-group">
                  <label>닉네임 *</label>
                  <input
                    type="text"
                    placeholder="예: 첫째, 둘째, 자녀1"
                    value={nickname}
                    onChange={(e) => setNickname(e.target.value)}
                    maxLength={50}
                    disabled={adding}
                    autoFocus
                  />
                </div>
                <div className="fm-form-group">
                  <label>생일 (선택)</label>
                  <input
                    type="date"
                    value={birthday}
                    onChange={(e) => setBirthday(e.target.value)}
                    disabled={adding}
                  />
                </div>
                <div className="fm-form-buttons">
                  <button
                    className="fm-btn-cancel"
                    onClick={() => { setShowAddForm(false); setNickname(''); setBirthday(''); }}
                    disabled={adding}
                  >
                    취소
                  </button>
                  <button
                    className="fm-btn-confirm"
                    onClick={handleAddChild}
                    disabled={adding}
                  >
                    {adding ? '추가 중...' : '추가'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="fm-action-buttons-row">
                <button className="fm-btn-add-child" onClick={() => setShowAddForm(true)}>
                  ➕ 자녀 추가
                </button>
                <button className="fm-btn-done" onClick={handleReset}>
                  ✅ 완료
                </button>
              </div>
            )}
            </div>{/* fm-right-area */}
          </div>
        )}
      </main>
    </div>
  );
};

export default FamilyManagePage;
